import {
    Box,
    Button,
    Flex,
    Heading,
    Text,
    useToast,
    VStack,
    HStack,
    Badge,
    Divider,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { useAuth } from "../../auth/AuthContext";
import {
    acceptDeliveryById,
    getDeliveryById,
} from "../../store/deliverySlice/deliverySlice";
import MapDisplay from "../../LocationsModule/MapDisplay";
import { useGoogleMaps } from "./DeliveryMap/useGoogleMaps";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const cardsBorderRadii = "8px";

const DeliveryDetails = () => {
    const { deliveryId } = useParams<{ deliveryId: string; }>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const { theme } = useCustomTheme();

    const delivery = useAppSelector((state) =>
        getDeliveryById(state, deliveryId || "")
    );

    const [directions, setDirections] =
        useState<google.maps.DirectionsResult | null>(null);
    const [distance, setDistance] = useState("");
    const [duration, setDuration] = useState("");
    const [cost, setCost] = useState<number | null>(null);
    const [center, setCenter] = useState({
        lat: -13.9626,
        lng: 33.7741,
    });

    const pricePerKm = 6;
    const { isLoaded, loadError } = useGoogleMaps();

    const origin = useMemo(() => {
        if (!delivery?.pickup) return null;
        return {
            lat: delivery.pickup.lat,
            lng: delivery.pickup.lng,
        };
    }, [delivery]);

    const destination = useMemo(() => {
        if (!delivery?.dropoff) return null;
        return {
            lat: delivery.dropoff.lat,
            lng: delivery.dropoff.lng,
        };
    }, [delivery]);

    useEffect(() => {
        if (!isLoaded || !origin || !destination) return;

        const svc = new google.maps.DirectionsService();
        svc.route(
            {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result) {
                    setDirections(result);
                    const leg = result.routes[0].legs[0];

                    const distText = leg.distance?.text || "";
                    const durText = leg.duration?.text || "";

                    setDistance(distText);
                    setDuration(durText);

                    const km = parseFloat(
                        distText.replace(/[^0-9.]/g, "") || "0"
                    );
                    setCost(Math.round(km * pricePerKm));

                    setCenter({
                        lat: leg.start_location.lat(),
                        lng: leg.start_location.lng(),
                    });
                }
            }
        );
    }, [isLoaded, origin, destination]);

    const handleAccept = async () => {
        if (!deliveryId || !user?.uid) return;

        try {
            await dispatch(
                acceptDeliveryById({
                    deliveryId,
                    driverId: user.uid,
                })
            ).unwrap();

            toast({
                title: "Delivery Accepted",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            navigate("/my-deliveries");
        } catch {
            toast({
                title: "Error",
                description: "Could not accept delivery.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    if (!delivery)
        return <Text textAlign="center" color={theme.text}>Loading delivery info...</Text>;

    // ✅ ONLY track when vehicle is moving
    const canTrack = delivery.status === "in_transit";

    const statusColorMap: Record<string, string> = {
        requested: "gray",
        assigned: "purple",
        in_transit: "green",
        delivered: "blue",
        cancelled: "red",
    };

    return (
        <Box bg={theme.surfaceAlt} minH="100vh" py={{ base: 4, md: 10 }} px={{ base: 3, md: 6 }}>
            <Flex
                maxW="1200px"
                mx="auto"
                direction={{ base: "column", lg: "row" }}
                gap={6}
            >
                {/* LEFT PANEL */}
                <Box
                    flex={1}
                    bg={theme.surface}
                    p={{ base: 4, md: 8 }}
                    borderRadius={cardsBorderRadii}
                    boxShadow={theme.shadowSm}
                    border="1px solid"
                    borderColor={theme.border}
                >
                    <Heading size="lg" mb={6} color={theme.text}>
                        Delivery Details
                    </Heading>

                    <VStack align="start" spacing={4}>
                        <Text color={theme.text}>
                            <strong>From:</strong>{" "}
                            {delivery.pickup?.address}
                        </Text>

                        <Text color={theme.text}>
                            <strong>To:</strong>{" "}
                            {delivery.dropoff?.address}
                        </Text>

                        <HStack>
                            <Text fontWeight="bold" color={theme.text}>Status:</Text>
                            <Badge
                                colorScheme={
                                    statusColorMap[delivery.status] || "gray"
                                }
                                px={3}
                                py={1}
                                borderRadius={cardsBorderRadii}
                            >
                                {delivery.status.replace("_", " ")}
                            </Badge>
                        </HStack>

                        <Divider borderColor={theme.divider} />

                        {cost !== null && (
                            <Text color={theme.text}>
                                <strong>Estimated Cost:</strong> MWK {cost}
                            </Text>
                        )}

                        {distance && (
                            <Text color={theme.text}>
                                <strong>Distance:</strong> {distance}
                            </Text>
                        )}

                        {duration && (
                            <Text color={theme.text}>
                                <strong>Duration:</strong> {duration}
                            </Text>
                        )}
                    </VStack>

                    <HStack mt={8} spacing={4} flexWrap="wrap">
                        {delivery.status === "requested" && (
                            <Button
                                px={6}
                                borderRadius={cardsBorderRadii}
                                onClick={handleAccept}
                                color={theme.textOnPrimary}
                                bgImage={theme.gradients.primaryButton}
                                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                                boxShadow={theme.shadowSm}
                            >
                                Accept Delivery
                            </Button>
                        )}

                        {canTrack && (
                            <Button
                                px={6}
                                borderRadius={cardsBorderRadii}
                                onClick={() =>
                                    navigate(`/live-trip/${delivery._id}`)
                                }
                                variant="outline"
                                borderColor={theme.borderStrong}
                                color={theme.text}
                                _hover={{ bg: theme.hover }}
                            >
                                Track Live
                            </Button>
                        )}
                    </HStack>
                </Box>

                {/* MAP PANEL */}
                <Box
                    flex={1}
                    bg={theme.surface}
                    p={{ base: 4, md: 6 }}
                    borderRadius={cardsBorderRadii}
                    boxShadow={theme.shadowSm}
                    border="1px solid"
                    borderColor={theme.border}
                >
                    {!isLoaded && <Text color={theme.textSubtle}>Loading map...</Text>}
                    {loadError && (
                        <Text color="red.500">
                            Failed to load Google Maps.
                        </Text>
                    )}

                    {isLoaded && directions && (
                        <MapDisplay
                            height="450px"
                            directions={directions}
                            center={center}
                        />
                    )}
                </Box>
            </Flex>
        </Box>
    );
};

export default DeliveryDetails;